/*
 * /compass-error — поправка компаса по светилу, девиация и перевод курсов.
 *
 * Раскладка (2026-09-13): исходные данные наблюдения — в рейке, всегда перед
 * глазами; четыре задачи — переключателем на сцене. Раньше место и время
 * стояли карточкой над вкладками, и в режиме девиации их приходилось листать
 * мимо, хотя девиации они не нужны.
 *
 * Что изменилось по существу:
 *  · СУДОВОЕ ВРЕМЯ И ПОЯС вместо «введите UTC». Судоводитель читает судовые
 *    часы, а ошибка в час — это ошибка азимута в десятки градусов, и именно её
 *    провоцировало поле «UTC».
 *  · СЕКУНДЫ. Ошибка азимута от неточного времени считается и показывается.
 *  · 28 навигационных звёзд, а не одна Полярная.
 *  · АМПЛИТУДА на истинном и видимом горизонте с высотой глаза.
 *  · ДЕВИАЦИЯ через коэффициенты A–E, три способа разворота (магнитный курс,
 *    сличение с гирокомпасом, створ), и девиация из таблицы подставляется в
 *    перевод курсов.
 *  · ЖУРНАЛ наблюдений; карточка девиации PDF и выгрузка журнала — Премиум.
 */

import * as E from './engine.js';
import { parseCoords, toDDM } from '../maritime/geodesy.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__CE || { T: {}, cfg: {} };
const t = makeT(B.T);
const LOC = B.cfg.locale || 'ru';
const SLUG = 'compass-error';
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const A = () => window.Astronomy || null;
const isPro = () => !!(window.__isProNow && window.__isProNow(false));
const store = {
    load: (k, d) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch { return d; } },
    save: (k, v) => { try { window.__toolSettings?.save(SLUG, k, v); } catch { } },
};
const funnel = (ev, d) => { try { window.__aeFunnel?.(ev, d || {}); } catch { } };

const S = {
    mode: 'azimuth',
    lat: 55.75, lon: 37.62,
    local: '', zone: 3, utc: new Date(), hasSeconds: false,
    body: 'Sun',
    eye: 15,
    varManual: null,
    instrument: 'gyro',
    observed: null,
    amp: { observed: null, which: 'geo' },
    swingMode: 'magnetic',
    swingCtx: { gyroError: 0, variation: 0, transitTrue: 0 },
    swing: [0, 45, 90, 135, 180, 225, 270, 315].map(c => ({ compass: c, ref: '' })),
    table: null,
    conv: { compass: 100, deviation: 0, variation: 0, true: 100, fromTable: true, gk: 100, gyroErr: 0, rel: 45, drift: 0 },
    journal: [],
    last: null,
};

/* ── рисование ───────────────────────────────────────────────────────────── */
const css = (name, fb) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fb;

function drawRose(canvas, trueAz, observed) {
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 240, H = canvas.clientHeight || 240;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const cx = W / 2, cy = H / 2, R = Math.min(W, H) * 0.42;
    const ink = getComputedStyle(canvas).color || '#889';
    ctx.strokeStyle = ink; ctx.globalAlpha = 0.35; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, 7); ctx.stroke();
    for (let a = 0; a < 360; a += 10) {
        const r = (a % 30 === 0) ? R * 0.88 : R * 0.94, rad = (a - 90) * Math.PI / 180;
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(rad) * r, cy + Math.sin(rad) * r);
        ctx.lineTo(cx + Math.cos(rad) * R, cy + Math.sin(rad) * R); ctx.stroke();
    }
    ctx.globalAlpha = 0.9; ctx.fillStyle = ink;
    ctx.font = '600 12px system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const [a, s] of [[0, 'N'], [90, 'E'], [180, 'S'], [270, 'W']]) {
        const rad = (a - 90) * Math.PI / 180;
        ctx.fillText(s, cx + Math.cos(rad) * R * 0.72, cy + Math.sin(rad) * R * 0.72);
    }
    ctx.globalAlpha = 1;
    const needle = (az, colour, dash, len) => {
        const rad = (az - 90) * Math.PI / 180;
        ctx.strokeStyle = colour; ctx.lineWidth = 2.6; ctx.setLineDash(dash ? [5, 4] : []);
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(rad) * R * len, cy + Math.sin(rad) * R * len); ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.arc(cx + Math.cos(rad) * R * len, cy + Math.sin(rad) * R * len, 4, 0, 7); ctx.fillStyle = colour; ctx.fill();
    };
    if (Number.isFinite(observed) && Number.isFinite(trueAz)) {
        const d = E.angleDiff(trueAz, observed);
        const a0 = (observed - 90) * Math.PI / 180, a1 = (observed + d - 90) * Math.PI / 180;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.arc(cx, cy, R * 0.46, a0, a1, d < 0); ctx.closePath();
        ctx.fillStyle = 'rgba(99,102,241,.25)'; ctx.fill();
    }
    if (Number.isFinite(observed)) needle(observed, '#e0a100', true, 0.84);
    if (Number.isFinite(trueAz)) needle(trueAz, '#16a34a', false, 0.98);
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, 7); ctx.fillStyle = ink; ctx.fill();
}

function drawDevCurve(canvas, table) {
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 600, H = canvas.clientHeight || 180;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const ink = getComputedStyle(canvas).color || '#889';
    const padL = 36, padR = 8, padT = 10, padB = 22;
    const w = W - padL - padR, h = H - padT - padB;
    const span = Math.max(2, Math.ceil((table?.maxAbs || 2) * 1.25));
    const x = deg => padL + (deg / 360) * w;
    const y = dev => padT + h / 2 - (dev / span) * (h / 2);
    ctx.font = '12px ui-monospace, Menlo, monospace'; ctx.fillStyle = ink; ctx.strokeStyle = ink;
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    for (const d of [-span, 0, span]) {
        ctx.globalAlpha = d === 0 ? 0.5 : 0.18;
        ctx.beginPath(); ctx.moveTo(padL, y(d)); ctx.lineTo(W - padR, y(d)); ctx.stroke();
        ctx.globalAlpha = 0.85;
        ctx.fillText((d > 0 ? '+' : '') + d + '°', padL - 5, y(d));
    }
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    for (let d = 0; d <= 360; d += 45) {
        ctx.globalAlpha = 0.15; ctx.beginPath(); ctx.moveTo(x(d), padT); ctx.lineTo(x(d), padT + h); ctx.stroke();
        ctx.globalAlpha = 0.85; ctx.fillText(String(d), x(d), padT + h + 5);
    }
    ctx.globalAlpha = 1;
    if (!table?.ok) return;
    ctx.beginPath();
    for (let d = 0; d <= 360; d += 3) { const py = y(table.at(d % 360)); d === 0 ? ctx.moveTo(x(d), py) : ctx.lineTo(x(d), py); }
    ctx.strokeStyle = '#6366f1'; ctx.lineWidth = 2.4; ctx.stroke();
    for (const p of table.points) {
        ctx.beginPath(); ctx.arc(x(p.ch), y(p.dev), 4.2, 0, 7); ctx.fillStyle = '#16a34a'; ctx.fill();
    }
}

/* ── общие данные ────────────────────────────────────────────────────────── */
function fmtShip(d) { return E.utcToShipTime(d, S.zone).replace('T', ' ').slice(0, S.hasSeconds ? 19 : 16); }
function fmtUtc(d) { return d.toISOString().slice(11, S.hasSeconds ? 19 : 16) + ' UTC'; }

function readCommon() {
    const p = parseCoords($('cePos').value);
    if (p) { S.lat = p.lat; S.lon = p.lon; }
    const d = toDDM(S.lat, S.lon);
    $('cePosOut').textContent = d.lat + '  ' + d.lon;
    S.local = $('ceTime').value;
    S.zone = Number($('ceZone').value) || 0;
    const u = E.shipTimeToUtc(S.local, S.zone);
    if (u) { S.utc = u.date; S.hasSeconds = u.hasSeconds; }
    $('ceUtcOut').textContent = t('utc_out', { utc: S.utc.toISOString().replace('T', ' ').slice(0, 19) });
    S.body = $('ceBody').value;
    S.eye = Math.max(0, Number($('ceEye').value) || 0);
    const v = E.variationAt(S.lat, S.lon, S.utc);
    const man = $('ceVarManual').checked ? Number($('ceVar').value) : null;
    S.varManual = Number.isFinite(man) ? man : null;
    $('ceVarOut').textContent = S.varManual !== null
        ? t('var_chart', { d: E.fmtDeg(S.varManual) + ' ' + E.ew(S.varManual) })
        : v ? t('var_here', { d: E.fmtDeg(v.d) + ' ' + E.ew(v.d), rate: v.rate.toFixed(2) }) : t('warn_no-wmm');
    const variation = S.varManual ?? (v ? v.d : 0);
    S.swingCtx.variation = Number(variation.toFixed(1));
    S.conv.variation = Number(variation.toFixed(1));
    const zh = E.zoneFromLon(S.lon);
    $('ceZoneHint').textContent = zh !== S.zone ? t('zone_hint', { z: (zh >= 0 ? '+' : '') + zh }) : '';
    store.save('common', { pos: $('cePos').value, zone: S.zone, body: S.body, eye: S.eye });
}

/* ═══ ПОПРАВКА ПО АЗИМУТУ ════════════════════════════════════════════════ */
function calcAzimuth() {
    const out = $('ceAzOut');
    out.textContent = '';
    if (!A()) { out.append(el('p', 'tc-msg bad', t('no_ephemeris'))); return; }
    const obs = Number.isFinite(S.observed) ? S.observed : null;
    const r = E.solve({
        A: A(), body: S.body, date: S.utc, lat: S.lat, lon: S.lon,
        observed: obs ?? 0, instrument: S.instrument,
        timeUncertaintyS: S.hasSeconds ? 2 : 30,
        variationOverride: S.varManual ?? undefined,
    });
    S.last = r;
    if (!r.ok) { out.append(el('p', 'tc-msg bad', t('no_ephemeris'))); return; }

    const hero = el('div', 'ce-hero');
    const ans = el('div', 'tc-answer');
    if (obs === null) {
        ans.append(el('p', 'tc-answer-l', t('f_true_az')), el('p', 'tc-answer-v', E.fmtBearing(r.trueAzimuth)),
            el('p', 'tc-answer-sub', t('enter_bearing')));
    } else {
        const main = S.instrument === 'magnetic' ? r.totalError : r.gyroError;
        ans.append(el('p', 'tc-answer-l', t(S.instrument === 'magnetic' ? 'f_total' : 'f_gyro')));
        const v = el('p', 'tc-answer-v', E.fmtDeg(main) + ' ');
        v.append(el('span', 'u', E.ew(main)));
        ans.append(v, el('p', 'tc-answer-sub', t('formula_line', { ic: E.fmtBearing(r.trueAzimuth), kk: E.fmtBearing(obs), d: E.fmtDeg(r.totalError) })));
        if (S.instrument === 'magnetic' && r.deviation !== null) {
            ans.append(el('p', 'tc-answer-note', t('dev_line', { dev: E.fmtDeg(r.deviation) + ' ' + E.ew(r.deviation), var: E.fmtDeg(r.variation) + ' ' + E.ew(r.variation) })));
        }
    }
    hero.append(ans);
    const rose = el('canvas', 'ce-rose');
    rose.setAttribute('role', 'img');
    rose.setAttribute('aria-label', t('rose_caption'));
    hero.append(rose);
    out.append(hero);

    const stats = el('div', 'tc-stats');
    const add = (l, v, s, cls) => { const f = el('div', 'tc-stat' + (cls ? ' ' + cls : '')); f.append(el('div', 'tc-stat-l', l), el('div', 'tc-stat-v', v)); if (s) f.append(el('div', 'tc-stat-s', s)); stats.append(f); };
    add(t('f_true_az'), E.fmtBearing(r.trueAzimuth), fmtShip(S.utc));
    add(t('f_alt'), r.altitude.toFixed(1) + '°', t('f_alt_sub'));
    if (r.azimuthRate !== null) add(t('f_rate'), (Math.abs(r.azimuthRate) * 60).toFixed(2) + '°/' + t('unit_min'),
        t('f_rate_sub', { err: r.timeError.toFixed(2), s: S.hasSeconds ? 2 : 30 }), r.timeError > 0.3 ? 'warn' : '');
    if (r.variation !== null) add(t('f_var'), E.fmtDeg(r.variation) + ' ' + E.ew(r.variation), t(r.variationSource === 'chart' ? 'f_var_chart' : 'f_var_sub'));
    out.append(stats);

    for (const w of r.warnings) out.append(el('p', 'tc-msg warn', t('warn_' + w)));

    if (obs !== null) {
        const acts = el('div', 'tc-actions ce-acts');
        const save = el('button', 'tc-btn ghost sm', t('journal_add'));
        save.type = 'button';
        save.addEventListener('click', () => addJournal(r, obs));
        acts.append(save);
        out.append(acts);
    }
    requestAnimationFrame(() => drawRose(rose, r.trueAzimuth, obs));
}

/* ── журнал ──────────────────────────────────────────────────────────────── */
function addJournal(r, obs) {
    S.journal.unshift({
        at: S.utc.toISOString(), zone: S.zone, lat: +S.lat.toFixed(4), lon: +S.lon.toFixed(4),
        body: S.body, instr: S.instrument, obs, true: +r.trueAzimuth.toFixed(1),
        err: +r.totalError.toFixed(1), dev: r.deviation != null ? +r.deviation.toFixed(1) : null,
        heading: Number($('ceHeading').value) || null,
    });
    S.journal = S.journal.slice(0, 500);
    store.save('journal', S.journal);
    renderJournal();
    window.__toast?.(t('journal_saved'), 'success');
    funnel('journal_add', { n: S.journal.length });
}

function renderJournal() {
    const box = $('ceJournal');
    box.textContent = '';
    $('ceJournalSub').textContent = S.journal.length ? String(S.journal.length) : '';
    if (!S.journal.length) { box.append(el('p', 'tc-hint', t('journal_empty'))); return; }
    const wrap = el('div', 'tc-tablewrap');
    const tb = el('table', 'tc-table');
    const hr = el('tr');
    for (const k of ['j_time', 'j_body', 'j_hdg', 'j_err', 'j_dev']) hr.append(el('th', null, t(k)));
    { const th = el('thead'); th.append(hr); tb.append(th); }
    const body = el('tbody');
    for (const j of S.journal.slice(0, 12)) {
        const tr = el('tr');
        tr.append(el('td', null, E.utcToShipTime(new Date(j.at), j.zone).replace('T', ' ').slice(0, 16)),
            el('td', null, t('body_' + j.body)),
            el('td', 'num', j.heading != null ? E.fmtBearing(j.heading, 0) : '—'),
            el('td', 'num', E.fmtDeg(j.err) + ' ' + (j.instr === 'gyro' ? t('j_gk') : t('j_mk'))),
            el('td', 'num', j.dev != null ? E.fmtDeg(j.dev) : '—'));
        body.append(tr);
    }
    tb.append(body);
    wrap.append(tb);
    box.append(wrap);
    /* Тренд поправки гирокомпаса — ради него журнал и ведут. */
    const gyro = S.journal.filter(j => j.instr === 'gyro').slice(0, 10);
    if (gyro.length >= 3) {
        const mean = gyro.reduce((s, j) => s + j.err, 0) / gyro.length;
        const sd = Math.sqrt(gyro.reduce((s, j) => s + (j.err - mean) ** 2, 0) / gyro.length);
        box.append(el('p', 'tc-note', t('journal_trend', { n: gyro.length, mean: E.fmtDeg(mean), sd: sd.toFixed(1) })));
    }
    const acts = el('div', 'tc-actions ce-acts');
    const csv = el('button', 'tc-btn ghost sm', t('journal_csv'));
    csv.type = 'button';
    csv.addEventListener('click', exportJournal);
    const clr = el('button', 'tc-btn ghost sm', t('journal_clear'));
    clr.type = 'button';
    clr.addEventListener('click', () => { if (window.confirm(t('journal_clear_confirm'))) { S.journal = []; store.save('journal', []); renderJournal(); } });
    acts.append(csv, clr);
    box.append(acts);
}

function upsell(what) {
    window.__toast?.(t('pro_locked'), 'info');
    const n = $('ceProNote');
    n.hidden = false;
    n.textContent = '';
    n.append(document.createTextNode(t('pro_locked') + ' '));
    const a = el('a', 'tc-perk', t('pro_link'));
    a.href = B.cfg.proUrl;
    n.append(a);
    funnel('perk_upsell', { what });
}

function exportJournal() {
    if (!isPro()) { upsell('journal_csv'); return; }
    const head = ['utc', 'zone', 'lat', 'lon', 'body', 'instrument', 'heading', 'compass_bearing', 'true_azimuth', 'total_error', 'deviation'];
    const rows = S.journal.map(j => [j.at, j.zone, j.lat, j.lon, j.body, j.instr, j.heading ?? '', j.obs, j.true, j.err, j.dev ?? '']);
    const csv = '\uFEFF' + [head, ...rows].map(r => r.join(',')).join('\r\n');
    window.__downloadFile?.(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'compass-error-journal.csv');
    funnel('journal_csv', { n: rows.length });
}

/* ═══ ВОСХОД / ЗАХОД ═════════════════════════════════════════════════════ */
function calcAmplitude() {
    const out = $('ceAmpOut');
    out.textContent = '';
    if (!A()) { out.append(el('p', 'tc-msg bad', t('no_ephemeris'))); return; }
    if (S.body === 'Polaris') { out.append(el('p', 'tc-msg', t('amp_polaris'))); return; }
    const mid = E.shipTimeToUtc((S.local || '').slice(0, 10) + 'T00:00', S.zone);
    const rows = E.riseSetDay(A(), S.body, mid ? mid.date : S.utc, S.lat, S.lon, S.eye);
    const wrap = el('div', 'tc-tablewrap');
    const tb = el('table', 'tc-table');
    const hr = el('tr');
    for (const k of ['a_event', 'a_geo', 'a_vis', 'a_amp']) hr.append(el('th', null, t(k)));
    { const th = el('thead'); th.append(hr); tb.append(th); }
    const body = el('tbody');
    for (const r of rows) {
        const tr = el('tr');
        tr.append(el('td', null, t('a_' + r.kind)));
        if (r.none) { const td = el('td', null, t('a_none')); td.colSpan = 3; tr.append(td); body.append(tr); continue; }
        const cell = x => {
            const td = el('td', 'num');
            if (!x) { td.textContent = '—'; return td; }
            td.append(el('b', null, E.fmtBearing(x.azimuth)), el('br'), el('span', 'tc-hint', fmtShip(x.date).slice(11) + ' · ' + fmtUtc(x.date)));
            return td;
        };
        tr.append(cell(r.geo), cell(r.vis));
        tr.append(el('td', 'num', r.geo && r.geo.amp ? `${Math.abs(r.geo.amp.amplitude).toFixed(1)}° ${r.geo.amp.from}${r.geo.amp.toward}` : '—'));
        body.append(tr);
    }
    tb.append(body);
    wrap.append(tb);
    out.append(wrap);
    S.ampRows = rows;

    const f = el('div', 'tc-fields ce-ampobs');
    const fld = (id, label, input) => { const w = el('div', 'tc-f'); const l = el('label', 'tc-f-l', label); l.htmlFor = id; w.append(l, input); return w; };
    const inp = el('input', 'tc-in'); inp.type = 'number'; inp.step = '0.1'; inp.id = 'ceAmpObs'; inp.inputMode = 'decimal';
    if (Number.isFinite(S.amp.observed)) inp.value = S.amp.observed;
    const sel = el('select', 'tc-in'); sel.id = 'ceAmpWhich';
    for (const [v, k] of [['rise-geo', 'amp_w_rise_geo'], ['rise-vis', 'amp_w_rise_vis'], ['set-geo', 'amp_w_set_geo'], ['set-vis', 'amp_w_set_vis']]) {
        const o = el('option', null, t(k)); o.value = v; sel.append(o);
    }
    sel.value = S.amp.which.includes('-') ? S.amp.which : 'rise-geo';
    f.append(fld('ceAmpObs', t('amp_obs'), inp), fld('ceAmpWhich', t('amp_which'), sel));
    out.append(f);
    const res = el('div', 'ce-ampres');
    out.append(res);
    const upd = () => {
        S.amp.observed = inp.value === '' ? null : Number(inp.value);
        S.amp.which = sel.value;
        res.textContent = '';
        const [kind, hz] = sel.value.split('-');
        const row = rows.find(x => x.kind === kind);
        const target = row && row[hz];
        if (!target || !Number.isFinite(S.amp.observed)) return;
        const err = E.angleDiff(target.azimuth, S.amp.observed);
        const ans = el('div', 'tc-answer');
        const v = el('p', 'tc-answer-v', E.fmtDeg(err) + ' ');
        v.append(el('span', 'u', E.ew(err)));
        ans.append(el('p', 'tc-answer-l', t('f_total')), v,
            el('p', 'tc-answer-sub', t('formula_line', { ic: E.fmtBearing(target.azimuth), kk: E.fmtBearing(S.amp.observed), d: E.fmtDeg(err) })));
        res.append(ans);
    };
    inp.addEventListener('input', upd);
    sel.addEventListener('change', upd);
    upd();
    out.append(el('p', 'tc-note', t('amp_note')));
}

/* ═══ ДЕВИАЦИЯ ═══════════════════════════════════════════════════════════ */
function renderSwing() {
    const body = $('ceSwingRows');
    body.textContent = '';
    $('ceRefHead').textContent = t('col_ref_' + S.swingMode);
    S.swing.forEach((row, i) => {
        const tr = el('tr');
        const mk = (val, step, ph, on) => {
            const inp = el('input', 'tc-in ce-cell'); inp.type = 'number'; inp.step = step; inp.value = val; inp.inputMode = 'decimal';
            if (ph) inp.placeholder = ph;
            inp.addEventListener('input', () => on(inp.value));
            const td = el('td'); td.append(inp); return td;
        };
        tr.append(mk(row.compass, '1', '', v => { S.swing[i].compass = Number(v); calcSwing(); }));
        tr.append(mk(row.ref, '0.1', '—', v => { S.swing[i].ref = v === '' ? '' : Number(v); calcSwing(); }));
        tr.append(el('td', 'num ce-devcell'));
        body.append(tr);
    });
    for (const id of ['ceSwGyro', 'ceSwVar', 'ceSwTransit']) {
        const box = $(id).closest('.tc-f');
        box.hidden = !((id === 'ceSwGyro' && S.swingMode === 'gyro') || (id === 'ceSwTransit' && S.swingMode === 'transit') || (id === 'ceSwVar' && S.swingMode !== 'magnetic'));
    }
    $('ceSwVar').value = S.swingCtx.variation;
    calcSwing();
}

function calcSwing() {
    const pts = E.swingPoints(S.swing, S.swingMode, S.swingCtx);
    const table = S.table = E.deviationTable(pts, Number($('ceStep').value) || 15);
    const byCh = new Map(pts.map(p => [p.ch, p.dev]));
    [...$('ceSwingRows').children].forEach((tr, i) => {
        const r = S.swing[i];
        const cell = tr.querySelector('.ce-devcell');
        const dv = byCh.get(E.norm360(Number(r.compass)));
        cell.textContent = r.ref === '' || dv == null ? '' : E.fmtDeg(dv);
    });
    const out = $('ceDevOut');
    out.textContent = '';
    drawDevCurve($('ceDevCurve'), table.ok ? table : null);
    if (!table.ok) { out.append(el('p', 'tc-hint', t('swing_need_two'))); store.save('swing', { mode: S.swingMode, ctx: S.swingCtx, rows: S.swing }); return; }
    const stats = el('div', 'tc-stats');
    const add = (l, v, s, cls) => { const f = el('div', 'tc-stat' + (cls ? ' ' + cls : '')); f.append(el('div', 'tc-stat-l', l), el('div', 'tc-stat-v', v)); if (s) f.append(el('div', 'tc-stat-s', s)); stats.append(f); };
    add(t('dev_max'), table.maxAbs.toFixed(1) + '°', t('dev_max_sub'), table.maxAbs > 5 ? 'bad' : table.maxAbs > 3 ? 'warn' : 'good');
    if (table.coef) {
        const c = table.coef;
        add('A', E.fmtDeg(c.A), t('coef_a'));
        add('B · C', `${E.fmtDeg(c.B)} · ${E.fmtDeg(c.C)}`, t('coef_bc'));
        add('D · E', `${E.fmtDeg(c.D)} · ${E.fmtDeg(c.E)}`, t('coef_de'));
        add(t('coef_rms'), '±' + c.rms.toFixed(2) + '°', t('coef_rms_sub'), c.rms > 1 ? 'warn' : '');
    }
    out.append(stats);
    out.append(el('p', 'tc-note', t(table.method === 'coef' ? 'dev_method_coef' : 'dev_method_linear')));
    if (table.maxAbs > 3) out.append(el('p', 'tc-msg warn', t('swing_note')));

    const wrap = el('div', 'tc-tablewrap');
    const tb = el('table', 'tc-table ce-devtable');
    const per = 6;
    for (let i = 0; i < table.table.length; i += per) {
        const trH = el('tr'), trV = el('tr');
        for (const row of table.table.slice(i, i + per)) {
            trH.append(el('th', 'num', String(row.compass).padStart(3, '0') + '°'));
            trV.append(el('td', 'num', E.fmtDeg(row.deviation)));
        }
        tb.append(trH, trV);
    }
    wrap.append(tb);
    out.append(wrap);
    const acts = el('div', 'tc-actions ce-acts');
    const pdf = el('button', 'tc-btn ghost sm', t('dev_card'));
    pdf.type = 'button';
    pdf.addEventListener('click', deviationCard);
    acts.append(pdf);
    out.append(acts);
    store.save('swing', { mode: S.swingMode, ctx: S.swingCtx, rows: S.swing });
    if (S.conv.fromTable) syncConv('compass');
}

async function deviationCard() {
    if (!isPro()) { upsell('deviation_card'); return; }
    const table = S.table;
    if (!table?.ok) return;
    const sheet = el('div', 'ce-card-sheet');
    sheet.append(el('h2', null, t('card_title')));
    const hd = el('table', 'hd');
    const tr = el('tr');
    for (const [k, id] of [['card_ship', 'ceCardShip'], ['card_compass', 'ceCardCompass'], ['card_place', null], ['card_date', null]]) {
        const td = el('td');
        td.append(el('b', null, t(k)), document.createTextNode(id ? ($(id).value || ' ') : k === 'card_place' ? $('cePosOut').textContent : window.__localYmd()));
        tr.append(td);
    }
    hd.append(tr);
    sheet.append(hd);
    const cv = el('canvas');
    cv.style.width = '1000px'; cv.style.height = '260px';
    cv.style.color = '#333';
    sheet.append(cv);
    const coef = table.coef;
    if (coef) sheet.append(el('p', null, `A ${E.fmtDeg(coef.A)}   B ${E.fmtDeg(coef.B)}   C ${E.fmtDeg(coef.C)}   D ${E.fmtDeg(coef.D)}   E ${E.fmtDeg(coef.E)}   ±${coef.rms.toFixed(2)}°`));
    /* Сетка из блоков, а не таблица: строки таблицы в снимке html2canvas разъезжались
       отдельными коробками. 12 курсов в ряд, под каждым — девиация. */
    const tb = el('div', 'grid');
    tb.style.gridTemplateColumns = 'repeat(' + Math.min(12, table.table.length) + ', 1fr)';
    for (const row of table.table) {
        const c = el('div', 'gc');
        c.append(el('b', null, String(row.compass).padStart(3, '0') + '°'), el('span', null, E.fmtDeg(row.deviation)));
        tb.append(c);
    }
    sheet.append(tb);
    const sign = el('div', 'sign');
    sign.append(el('div', null, t('card_sign')), el('div', null, t('card_date')));
    sheet.append(sign);
    const host = $('ceCardHost');
    host.textContent = '';
    host.append(sheet);
    host.hidden = false;
    drawDevCurve(cv, table);
    funnel('deviation_card', {});
    sheet.remove();
    try {
        const res = await window.__generatePDF?.({ element: sheet, asIs: true, orientation: 'landscape', filename: 'deviation-card.pdf', pagebreak: { mode: ['css', 'legacy'] } });
        if (res && res.ok === false) window.__toast?.(t('pdf_error'), 'error');
    } finally { host.hidden = true; }
}

/* ═══ ПЕРЕВОД КУРСОВ ═════════════════════════════════════════════════════ */
function syncConv(from) {
    const c = S.conv;
    if (c.fromTable && S.table?.ok) c.deviation = Number(S.table.at(c.compass).toFixed(1));
    if (from === 'true') {
        const r = E.trueToCompass(c.true, c.deviation, c.variation);
        c.magnetic = r.magnetic; c.compass = r.compass;
        if (c.fromTable && S.table?.ok) {
            /* Девиация зависит от компасного курса, а он зависит от девиации:
               две итерации сходятся до сотых. */
            for (let k = 0; k < 3; k++) {
                c.deviation = Number(S.table.at(c.compass).toFixed(1));
                const r2 = E.trueToCompass(c.true, c.deviation, c.variation);
                c.magnetic = r2.magnetic; c.compass = r2.compass;
            }
        }
    } else if (from === 'gyro') {
        c.true = E.norm360(c.gk + c.gyroErr);
        return syncConv('true');
    } else {
        const r = E.compassToTrue(c.compass, c.deviation, c.variation);
        c.magnetic = r.magnetic; c.true = r.true;
    }
    const setv = (id, v) => { const n = $(id); if (n && document.activeElement !== n) n.value = Number(v).toFixed(1); };
    setv('ceConvC', c.compass); setv('ceConvT', c.true); setv('ceConvD', c.deviation); setv('ceConvV', c.variation);
    $('ceConvD').disabled = c.fromTable && !!S.table?.ok;
    $('ceConvM').textContent = E.fmtBearing(c.magnetic);
    $('ceConvChain').textContent = t('conv_chain', {
        kk: E.fmtBearing(c.compass), dev: E.fmtDeg(c.deviation),
        mk: E.fmtBearing(c.magnetic), var: E.fmtDeg(c.variation), ik: E.fmtBearing(c.true),
    });
    $('ceConvTrueBrg').textContent = E.fmtBearing(E.norm360(c.true + c.rel));
    $('ceConvTrack').textContent = E.fmtBearing(E.norm360(c.true + c.drift));
    store.save('conv', c);
}

/* ── режимы и подключение ────────────────────────────────────────────────── */
function setMode(m) {
    S.mode = m;
    for (const b of document.querySelectorAll('.ce-mode')) { const on = b.dataset.mode === m; b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); }
    for (const p of document.querySelectorAll('.ce-pane')) p.hidden = p.dataset.mode !== m;
    recalc();
    store.save('mode', m);
    window.__stage?.('mode_' + m);
}

function recalc() {
    readCommon();
    if (S.mode === 'azimuth') calcAzimuth();
    if (S.mode === 'amplitude') calcAmplitude();
    if (S.mode === 'deviation') renderSwing();
    if (S.mode === 'convert') syncConv('compass');
}

function setNow() {
    const n = new Date();
    $('ceTime').value = E.utcToShipTime(n, Number($('ceZone').value) || 0).slice(0, 19);
}

function bind() {
    for (const b of document.querySelectorAll('.ce-mode')) b.addEventListener('click', () => setMode(b.dataset.mode));
    let deb;
    const soon = () => { clearTimeout(deb); deb = setTimeout(recalc, 180); };
    $('cePos').addEventListener('input', soon);
    $('ceTime').addEventListener('input', recalc);
    $('ceZone').addEventListener('change', recalc);
    $('ceEye').addEventListener('input', soon);
    $('ceBody').addEventListener('change', recalc);
    $('ceVarManual').addEventListener('change', () => { $('ceVar').closest('.tc-f').hidden = !$('ceVarManual').checked; recalc(); });
    $('ceVar').addEventListener('input', soon);
    $('ceNow').addEventListener('click', () => { setNow(); recalc(); });
    $('ceHere').addEventListener('click', () => {
        if (!navigator.geolocation) { window.__toast?.(t('geo_unavailable'), 'warning'); return; }
        navigator.geolocation.getCurrentPosition(
            p => { $('cePos').value = `${p.coords.latitude.toFixed(5)} ${p.coords.longitude.toFixed(5)}`; recalc(); },
            () => window.__toast?.(t('geo_denied'), 'warning'),
            { enableHighAccuracy: false, timeout: 8000 });
    });
    for (const b of document.querySelectorAll('.ce-instr')) {
        b.addEventListener('click', () => {
            S.instrument = b.dataset.v;
            for (const x of document.querySelectorAll('.ce-instr')) x.classList.toggle('on', x === b);
            store.save('instr', S.instrument);
            calcAzimuth();
        });
    }
    $('ceObserved').addEventListener('input', e => { S.observed = e.target.value === '' ? null : Number(e.target.value); calcAzimuth(); });
    for (const b of document.querySelectorAll('.ce-swmode')) {
        b.addEventListener('click', () => {
            S.swingMode = b.dataset.v;
            for (const x of document.querySelectorAll('.ce-swmode')) x.classList.toggle('on', x === b);
            renderSwing();
        });
    }
    $('ceSwGyro').addEventListener('input', e => { S.swingCtx.gyroError = Number(e.target.value) || 0; calcSwing(); });
    $('ceSwVar').addEventListener('input', e => { S.swingCtx.variation = Number(e.target.value) || 0; calcSwing(); });
    $('ceSwTransit').addEventListener('input', e => { S.swingCtx.transitTrue = Number(e.target.value) || 0; calcSwing(); });
    $('ceStep').addEventListener('change', calcSwing);

    const num = id => Number($(id).value) || 0;
    $('ceConvC').addEventListener('input', () => { S.conv.compass = num('ceConvC'); syncConv('compass'); });
    $('ceConvT').addEventListener('input', () => { S.conv.true = num('ceConvT'); syncConv('true'); });
    $('ceConvD').addEventListener('input', () => { S.conv.deviation = num('ceConvD'); syncConv('compass'); });
    $('ceConvV').addEventListener('input', () => { S.conv.variation = num('ceConvV'); syncConv('compass'); });
    $('ceConvFromTable').addEventListener('change', e => { S.conv.fromTable = e.target.checked; syncConv('compass'); });
    $('ceConvGk').addEventListener('input', () => { S.conv.gk = num('ceConvGk'); syncConv('gyro'); });
    $('ceConvGe').addEventListener('input', () => { S.conv.gyroErr = num('ceConvGe'); syncConv('gyro'); });
    $('ceConvRel').addEventListener('input', () => { S.conv.rel = num('ceConvRel'); syncConv('compass'); });
    $('ceConvDrift').addEventListener('input', () => { S.conv.drift = num('ceConvDrift'); syncConv('compass'); });

    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (S.mode === 'azimuth') calcAzimuth(); if (S.mode === 'deviation' && S.table) drawDevCurve($('ceDevCurve'), S.table.ok ? S.table : null); }, 160); });
}

function restore() {
    const c = store.load('common', null);
    if (c) {
        if (c.pos) $('cePos').value = c.pos;
        if (Number.isFinite(c.zone)) $('ceZone').value = String(c.zone);
        if (c.body && E.BODIES.includes(c.body)) $('ceBody').value = c.body;
        if (Number.isFinite(c.eye)) $('ceEye').value = String(c.eye);
    } else {
        /* Первый визит: пояс — по часам устройства, это лучшая догадка, пока
           человек не выбрал судовой. */
        $('ceZone').value = String(Math.round(-new Date().getTimezoneOffset() / 30) / 2);
    }
    S.instrument = store.load('instr', 'gyro') === 'magnetic' ? 'magnetic' : 'gyro';
    for (const x of document.querySelectorAll('.ce-instr')) x.classList.toggle('on', x.dataset.v === S.instrument);
    const sw = store.load('swing', null);
    if (sw && Array.isArray(sw.rows) && sw.rows.length) { S.swing = sw.rows; S.swingMode = sw.mode || 'magnetic'; Object.assign(S.swingCtx, sw.ctx || {}); }
    for (const x of document.querySelectorAll('.ce-swmode')) x.classList.toggle('on', x.dataset.v === S.swingMode);
    $('ceSwGyro').value = S.swingCtx.gyroError; $('ceSwTransit').value = S.swingCtx.transitTrue;
    const cv = store.load('conv', null);
    if (cv) Object.assign(S.conv, cv);
    $('ceConvFromTable').checked = !!S.conv.fromTable;
    for (const [id, k] of [['ceConvGk', 'gk'], ['ceConvGe', 'gyroErr'], ['ceConvRel', 'rel'], ['ceConvDrift', 'drift']]) $(id).value = S.conv[k];
    S.journal = store.load('journal', []) || [];
    return store.load('mode', 'azimuth');
}

function boot() {
    try {
        const mode = restore();
        setNow();
        bind();
        renderJournal();
        setMode(['azimuth', 'amplitude', 'deviation', 'convert'].includes(mode) ? mode : 'azimuth');
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'compass-error');
        const b = $('ceAzOut');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
